// MUST be first: lift an Open Banking ?code=&state= out of the URL before the
// Supabase client (imported transitively by App) can consume it. See the file.
import "./utils/bankCallbackCapture"
import { Capacitor } from "@capacitor/core"
import { createApp } from "vue"
import App from "./App.vue"
import "./style.css"
import i18n from "./i18n"
import { installAndroidInsets } from "./utils/androidInsets"
import { installChunkReloadGuard } from "./utils/chunkReload"
import { installConsoleCapture } from "./utils/consoleCapture"
import { initSentry } from "./utils/sentry"

// Start buffering console output as early as possible so user feedback can
// attach the lead-up logs (see utils/feedbackContext.ts).
installConsoleCapture()

// Recover from stale lazy chunks after a deploy: a missing hashed chunk gets
// the SPA index.html back, which can't run as a module ("'text/html' is not a
// valid JavaScript MIME type"). Reload once to pick up the new build.
installChunkReloadGuard()

// Pull the native window insets before first paint — MainActivity's push
// lands before this document exists. See utils/androidInsets.ts.
installAndroidInsets()

// PWA service worker — web only. Inside the native Capacitor shell the
// workbox precache would outlive app-binary updates and serve the previous
// bundle's index.html (stale-content/white-screen footgun), so native skips
// registration and unregisters any SW left behind by earlier builds.
if ("serviceWorker" in navigator) {
  if (Capacitor.isNativePlatform()) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        for (const registration of registrations) registration.unregister()
      })
      .catch(() => {})
  } else {
    import("virtual:pwa-register").then(({ registerSW }) => registerSW({ immediate: true })).catch(() => {})
  }
}

const app = createApp(App)

// Initialize Sentry for error tracking (production only)
initSentry(app)

app.use(i18n).mount("#app")
